'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { formatDuration } from 'date-fns';

import PrimaryButton from '@/components/theme/PrimaryButton';
import NotAvailableStatus from '@/components/theme/not-available-status';
import {
  CheckInSettings,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import useLocalization from '@/hooks/useLocalization';

import {
  getCheckInLabelFromSettings,
  getCheckInUnavailableLabelFromSettings,
} from '../utils';
import { getCheckInAvailability } from '../../helper/reservationHelper';
import Link from 'next/link';
import { Box } from '@ui-library';

interface CheckInButtonProps {
  reservation: ReservationWithRateDetailsDTO;
  settings: Settings;
  redirectPath: string;
  preview?: boolean;
  checkInSettings: CheckInSettings;
}

const CheckInButton = ({
  reservation,
  redirectPath,
  settings,
  preview,
  checkInSettings,
}: CheckInButtonProps): React.ReactElement | null => {
  const intl = useIntl();
  const { dateFnsLocale: locale } = useLocalization();

  const hoursBeforeArrival = (checkInSettings.days_before_arrival || 1) * 24;
  const { canCheckIn, isTooEarly } = getCheckInAvailability(
    reservation,
    checkInSettings.days_before_arrival,
    settings.whistle_guest_company_timezone,
  );

  if (!canCheckIn || !settings?.features?.length) {
    return null;
  }

  if (isTooEarly) {
    return (
      <NotAvailableStatus
        label={getCheckInUnavailableLabelFromSettings(
          intl,
          checkInSettings.check_in_btn_label,
          formatDuration({ hours: hoursBeforeArrival }, { locale }),
        )}
      />
    );
  }

  const linkPath = preview ? '' : redirectPath;

  return (
    <Box mt="4">
      <Link href={linkPath} prefetch={true}>
        <PrimaryButton settings={settings} fullWidth>
          {getCheckInLabelFromSettings(
            intl,
            checkInSettings.check_in_btn_label,
          )}
        </PrimaryButton>
      </Link>
    </Box>
  );
};

export default CheckInButton;
